import type { TFunction } from "i18next"
import React, { createContext, useContext } from "react"

import useMe from "@hooks/use_me"
import i18next from "@utils/localization"
import { User } from "__generated__/graphql"
import useTogglePersist from "hooks/use_toggle_persist"
import useTranslate from "hooks/use_translate"

export type AppContextProps = {
  translate: TFunction<"translation", undefined>
  toggleAppBarOpen?: () => void
  isAppBarOpen?: boolean
  setMe?: React.Dispatch<React.SetStateAction<User | undefined>>
  me?: User | undefined
  logoutUser?: () => void
}

export const appContextDefaultValues: AppContextProps = {
  translate: i18next.t
}

const AppContext = createContext(appContextDefaultValues)

export function AppContextProvider(props: {
  children: React.ReactNode | React.ReactNode[]
}) {
  const { translate } = useTranslate()
  const [isAppBarOpen, toggleAppBarOpen] = useTogglePersist("app_bar", false)
  const { me, setMe } = useMe()
  function logoutUser() {
    if (!me) {
      return
    }
    const oldLang = localStorage.getItem("i18nextLng") || "en"
    localStorage.clear()
    localStorage.setItem("i18nextLng", oldLang)
  }

  return (
    <AppContext.Provider
      value={{
        ...appContextDefaultValues,
        translate,
        isAppBarOpen,
        toggleAppBarOpen,
        setMe,
        logoutUser,
        me
      }}
    >
      {props.children}
    </AppContext.Provider>
  )
}

export function useAppContext() {
  const context = useContext(AppContext)
  if (context === undefined) {
    throw new Error("App Context Error")
  }
  return context
}
